import { useCallback } from 'react';

import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';

import type { DraftWorkItem } from '../common/types';
import { saveDraftWorkItem } from '../services/draft-work-items/index.ts';

import { useDraftWorkItemsActions, useDraftWorkItemsState } from './draft-work-items-store';
import { useJiraContext } from './useJiraContext';

const EMPTY_DRAFT_WORK_ITEMS_MAP: Record<string, DraftWorkItem> = {};

/**
 * This hook functions as a thin wrapper around the draft-work-items-store.
 * Selectors are called at render time (Rules of Hooks compliant) and the
 * returned getter functions close over the already-computed values.
 *
 * For now, we're keeping the same shape as the original useDraftWorkItems hook
 * to limit the impact of this change.
 *
 * When we clean up the the sync-state feature flag,
 * we can remove this hook and use `useDraftWorkItemsSelectors` / `createStateHook` directly.
 * This refactor will be done in a separate PR to keep this FG clean up easy to review.
 */
export const useDraftWorkItems = ({
	messageId,
	isActiveMessage,
	conversationId,
	cloudId,
	sendAifcAnalyticsEvent,
}: {
	messageId: string;
	isActiveMessage?: boolean;
	conversationId?: string;
	cloudId?: string;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
}): {
	onWorkItemsCreated: ({
		invocationIdToCreatedIssueKey,
	}: {
		invocationIdToCreatedIssueKey: Record<string, string>;
	}) => void;
	onDismissAllDraftWorkItems: () => void;
	onFieldChange: (invocationId: string, fieldId: string, newValue: unknown) => void;
	removeWorkItem: (invocationId: string) => boolean;
} => {
	const { messages } = useDraftWorkItemsState();
	const messageState = messages[messageId];
	const draftWorkItems = messageState?.draftWorkItemsMap ?? EMPTY_DRAFT_WORK_ITEMS_MAP;
	const storeActions = useDraftWorkItemsActions();
	const experienceTracker = useAIMateExperienceTracker();

	// TODO: Fix this usage so that we correctly send current state (including inline edits) as JiraContext
	useJiraContext({ draftWorkItems, isActiveMessage });

	const onWorkItemsCreated = useCallback(
		({
			invocationIdToCreatedIssueKey,
		}: {
			invocationIdToCreatedIssueKey: Record<string, string>;
		}): void => {
			storeActions.onDraftWorkItemsCreated(messageId, invocationIdToCreatedIssueKey);
		},
		[storeActions, messageId],
	);

	const removeWorkItem = useCallback(
		(invocationId: string): boolean => {
			return storeActions.removeDraftWorkItem(messageId, invocationId);
		},
		[storeActions, messageId],
	);

	const onDismissAllDraftWorkItems = useCallback((): void => {
		storeActions.onDismissAllDraftWorkItems(messageId);
	}, [storeActions, messageId]);

	const onFieldChange = useCallback(
		(invocationId: string, fieldId: string, newValue: unknown, skipFireAnalytics = false): void => {
			const draftWorkItem = draftWorkItems[invocationId];

			// If we are skipping the analytics firing event, it is not a user action and hence we don't need to save the draft work item
			if (draftWorkItem && cloudId && conversationId && !skipFireAnalytics) {
				const payload = {
					invocationId,
					data: {
						summary: fieldId === 'summary' ? String(newValue) : draftWorkItem.summary,
						issueTypeId: draftWorkItem.issueTypeId,
						projectIdOrKey: draftWorkItem.projectIdOrKey,
						fields: { ...draftWorkItem.fields, [fieldId]: newValue },
					},
				};
				saveDraftWorkItem(
					cloudId,
					conversationId,
					messageId,
					payload,
					sendAifcAnalyticsEvent,
					experienceTracker,
				);
			}

			storeActions.onFieldChange(messageId, invocationId, fieldId, newValue);
		},
		[
			storeActions,
			messageId,
			cloudId,
			conversationId,
			sendAifcAnalyticsEvent,
			experienceTracker,
			draftWorkItems,
		],
	);

	return {
		onWorkItemsCreated,
		onDismissAllDraftWorkItems,
		onFieldChange,
		removeWorkItem,
	};
};
